import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  Brain,
  User,
  Robot,
  ChatCircle,
  Lightning,
  Clock,
  ArrowsClockwise,
  Fingerprint,
  CaretRight,
  Trash,
  Eye,
  ArrowLeft,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const ENTITY_TABS = [
  { id: 'customer', label: 'Customer', Icon: User },
  { id: 'worker', label: 'Worker', Icon: Robot },
  { id: 'interaction', label: 'Interaction', Icon: ChatCircle },
];

function timeAgo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function MemoryValue({ value }) {
  if (value === null || value === undefined) return <span className="text-[#5C6778]">null</span>;
  if (typeof value === 'string') return <span className="text-[#F0F4F8]">{value}</span>;
  if (typeof value === 'number' || typeof value === 'boolean')
    return <span className="text-[#00E5C0]">{String(value)}</span>;
  return (
    <pre className="text-[11px] text-[#9CA3B0] bg-[#0A0C10] rounded px-2 py-1.5 overflow-x-auto max-h-32 whitespace-pre-wrap break-all" data-testid="memory-value-json">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function MemoryRecord({ record }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="border border-[rgba(255,255,255,0.04)] rounded-lg bg-[#1F2937] hover:border-[rgba(255,255,255,0.08)] transition-colors"
      data-testid={`memory-record-${record.key}`}
    >
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid={`memory-record-toggle-${record.key}`}
      >
        <CaretRight
          size={12}
          className={`text-[#5C6778] transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-[#F0F4F8]">{record.key}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.03)] text-[#7D8896] border border-[rgba(255,255,255,0.04)]">
              {record.memory_type}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-[#5C6778] flex items-center gap-1">
              <Clock size={10} /> {timeAgo(record.updated_at)}
            </span>
            {record.confidence !== undefined && record.confidence < 1 && (
              <span className="text-[10px] text-[#5C6778]">
                conf: {(record.confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
        </div>
        {typeof record.value === 'string' && (
          <span className="text-[11px] text-[#7D8896] truncate max-w-[200px]">{record.value}</span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-3 border-t border-[rgba(255,255,255,0.03)]">
          <div className="pt-3 space-y-2">
            <div>
              <span className="text-[10px] text-[#5C6778] uppercase tracking-wider">Value</span>
              <div className="mt-1"><MemoryValue value={record.value} /></div>
            </div>
            <div className="flex gap-6 text-[10px] text-[#5C6778]">
              <div>
                <span className="uppercase tracking-wider">Source Run</span>
                <div className="mt-0.5 text-[#7D8896] font-mono" data-testid="memory-source-run">
                  {record.source_run_id || '—'}
                </div>
              </div>
              <div>
                <span className="uppercase tracking-wider">Worker</span>
                <div className="mt-0.5 text-[#7D8896] font-mono">{record.worker_id || '—'}</div>
              </div>
              <div>
                <span className="uppercase tracking-wider">Created</span>
                <div className="mt-0.5 text-[#7D8896]">
                  {record.created_at ? new Date(record.created_at).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <span className="uppercase tracking-wider">Updated</span>
                <div className="mt-0.5 text-[#7D8896]">
                  {record.updated_at ? new Date(record.updated_at).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MemoryInspector() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('customer');
  const [entityId, setEntityId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const placeholders = {
    customer: 'Enter customer email or phone...',
    worker: 'Enter worker ID...',
    interaction: 'Enter thread ID or customer email...',
  };

  const fetchRecords = useCallback(async (type, id) => {
    if (!id.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      let url;
      if (type === 'customer') url = `${BACKEND_URL}/api/memory/customer/${encodeURIComponent(id)}`;
      else if (type === 'worker') url = `${BACKEND_URL}/api/memory/worker/${encodeURIComponent(id)}`;
      else url = `${BACKEND_URL}/api/memory/interaction/${encodeURIComponent(id)}`;

      const res = await fetchWithOrg(url);
      const data = await res.json();
      setRecords(data.records || []);
    } catch (err) {
      toast.error('Failed to load memory');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = () => {
    const id = searchInput.trim();
    if (id) {
      setEntityId(id);
      fetchRecords(tab, id);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = async () => {
    if (!entityId) return;
    const ok = window.confirm(`Delete all ${tab} memory for "${entityId}"?`);
    if (!ok) return;
    try {
      let url;
      if (tab === 'worker') url = `${BACKEND_URL}/api/memory/worker/${encodeURIComponent(entityId)}`;
      else url = `${BACKEND_URL}/api/memory/entity/${tab}/${encodeURIComponent(entityId)}`;
      await fetchWithOrg(url, { method: 'DELETE' });
      toast.success('Memory cleared');
      setRecords([]);
    } catch {
      toast.error('Failed to delete memory');
    }
  };

  // Reset when switching tabs
  useEffect(() => {
    setRecords([]);
    setSearched(false);
    setEntityId('');
    setSearchInput('');
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#080A0E]" data-testid="memory-inspector-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Back nav */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate('/workers')}
            data-testid="back-to-workers-btn"
            className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors"
          >
            <ArrowLeft size={14} />
            Workers
          </button>
        </div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2.5 mb-1">
            <Brain size={20} weight="duotone" className="text-[#00E5C0]" />
            <h1 className="text-xl font-semibold text-[#F0F4F8]" data-testid="memory-inspector-title">
              Memory Inspector
            </h1>
          </div>
          <p className="text-sm text-[#7D8896]">
            Inspect structured execution memory across customers, workers, and interactions.
          </p>
        </div>

        {/* Entity type tabs */}
        <div className="flex gap-1 mb-6 bg-[#1F2937] p-1 rounded-lg border border-[rgba(255,255,255,0.04)] w-fit" data-testid="entity-tabs">
          {ENTITY_TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${
                tab === id
                  ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)]'
                  : 'text-[#7D8896] hover:text-[#9CA3B0]'
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon size={14} weight={tab === id ? 'fill' : 'regular'} />
              {label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-6" data-testid="memory-search">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6778]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholders[tab]}
              className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-[#1F2937] border border-[rgba(255,255,255,0.08)] text-sm text-[#F0F4F8] placeholder:text-[#5C6778] outline-none focus:border-[rgba(0,229,192,0.3)] transition-colors"
              data-testid="memory-search-input"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={!searchInput.trim() || loading}
            className="px-4 py-2.5 rounded-lg bg-[rgba(0,229,192,0.08)] text-[#00E5C0] text-xs font-medium border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.12)] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
            data-testid="memory-search-btn"
          >
            {loading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Eye size={14} />}
            Inspect
          </button>
        </div>

        {/* Results */}
        {searched && !loading && (
          <div data-testid="memory-results">
            {/* Results header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Fingerprint size={14} className="text-[#5C6778]" />
                <span className="text-xs text-[#7D8896] font-mono">{entityId}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.03)] text-[#5C6778] border border-[rgba(255,255,255,0.04)]">
                  {records.length} record{records.length !== 1 ? 's' : ''}
                </span>
              </div>
              {records.length > 0 && (
                <button
                  onClick={handleDelete}
                  className="text-[10px] text-[#5C6778] hover:text-red-400 flex items-center gap-1 transition-colors"
                  data-testid="memory-delete-btn"
                >
                  <Trash size={11} /> Clear
                </button>
              )}
            </div>

            {records.length === 0 ? (
              <div className="text-center py-12 border border-[rgba(255,255,255,0.04)] rounded-lg bg-[#1F2937]" data-testid="memory-empty-state">
                <Brain size={28} className="text-[#2A2E35] mx-auto mb-2" />
                <p className="text-sm text-[#5C6778]">No memory records found</p>
                <p className="text-[10px] text-[#2A2E35] mt-1">
                  Memory is written after worker executions complete
                </p>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="memory-records-list">
                {records.map((r, i) => (
                  <MemoryRecord key={r.memory_id || `${r.key}-${i}`} record={r} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Help text when nothing searched */}
        {!searched && (
          <div className="text-center py-16" data-testid="memory-initial-state">
            <Brain size={32} className="text-[#1A1E24] mx-auto mb-3" />
            <p className="text-sm text-[#5C6778] mb-1">Search for a {tab} to inspect memory</p>
            <p className="text-[11px] text-[#2A2E35] max-w-sm mx-auto">
              {tab === 'customer' && 'Enter a customer email or phone to see their interaction history, preferences, and outcomes.'}
              {tab === 'worker' && 'Enter a worker ID to see its last runs, escalation patterns, and outcome summaries.'}
              {tab === 'interaction' && 'Enter a thread ID or customer identifier to see message context, CRM refs, and action history.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
